import {
    Component,
    Input,
    ViewChild,
    EventEmitter,
    Output,
    ElementRef,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { DomHandler } from 'primeng/dom';

import { ActionKeys } from '../../enums/keys.enum';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { ButtonTypesEnum } from '../../enums/button-types.enum';
import { ChipModel } from '../../models/chip.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { FocusHelper } from '../../helpers/focus.helper';
import { Popover } from 'primeng/popover';

/**
 * ### Accessibility Usage
 * + ***ariaLabelledBy*** - use this to link an external label to the chip list
 * + ***isInTable*** - use this when the chip container is in a table
 */
@Component({
    selector: 'agpr-chip-container',
    templateUrl: './chip-container.component.html',
    styleUrls: ['./chip-container.component.scss'],
    standalone: false
})
export class ChipContainerComponent implements OnChanges {

    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly buttonTypes = ButtonTypesEnum;

    @Input() id: string;

    // The holder of chips
    @Input() chips: (ChipModel | string)[] = [];
    @Input() chipGroups: {
        label: string;
        chips: ChipModel[];
    }[] = [];
    @Input() readonly: boolean = false;
    @Input() plainMode: boolean = false;
    @Input() deleteIcon: string = 'fa-regular fa-duotone fa-xmark';

    @Input() overflowMessage: string = 'ChipContainer.showAll';
    @Input() emptyMessage: string;
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.social;
    @Input() noContainerWhenEmpty: boolean = false;
    @Input() noTooltips: boolean = false;
    @Input() isInTable: boolean = false;
    @Input() wrapChips: boolean = true;
    @Input() ariaLabelledBy: string = '';

    @Output() removeChip: EventEmitter<number> = new EventEmitter<number>();

    @ViewChild('chipPopover') chipPopover: Popover;
    @ViewChild('chipsContainer') chipsContainer: ElementRef;

    areChipsOverflowing: boolean = false;

    // Groups shown in the popover; falls back to a single unlabelled group when only a flat chips list is provided.
    popoverChipGroups: { label: string; chips: ChipModel[] }[] = [];

    constructor(){}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.hasOwnProperty('chips') || changes.hasOwnProperty('chipGroups')) {
            this.popoverChipGroups = this.chipGroups.length > 0
                ? this.chipGroups
                : [{ label: '', chips: this.chips as ChipModel[] }];
        }

        if (changes.hasOwnProperty('chips') && this.chipPopover?.overlayVisible) {
            if (this.chips.length === 0) {
                this.chipPopover.hide();
            }
        }
    }

    private panelKeydownListener = (event) => {
        this.onPanelKeydown(event);
    };
    /**
     * Emits a removeChip event with a number to indicate the chip to be removed.
     * The event will need to be handled in the parent component
     *
     * @param index the position of the chip that needs to be removed in the parent component
     * @param parent the list that holds the chip
     */
    onRemoveChip(index: number, parent: Element) {
        this.removeChip.emit(index);
        if (this.chips.length > 0) {
            FocusHelper.handleFocusAfterChipRemoval(parent, index, 'button');
        }
    }

    // Maps the grouped chip index to the flat chip list for removal and focus handling.
    onRemoveGroupedChip(chip: ChipModel | string, index: number, parent: Element) {
        const flatIndex = this.chips.indexOf(chip);
        if (flatIndex === -1) {
            return;
        }
        this.removeChip.emit(flatIndex);
        if (this.chips.length > 0) {
            FocusHelper.handleFocusAfterChipRemoval(parent, index, 'button');
        }
    }

    /**
     * Handles event of chips overflowing list element
     */
    areChipsOverflowingChange(event) {
        if (this.plainMode) {
            return;
        }

        this.areChipsOverflowing = event;

        if (!this.areChipsOverflowing && this.chipPopover.overlayVisible) {
            this.chipPopover.hide();
            this.onHide();
        }
    }

    showPanel(event, chipsContainer) {
        this.chipPopover.baseZIndex = ++DomHandler.zindex;
        this.chipPopover.toggle(event, chipsContainer);

        requestAnimationFrame(() => {
            let chipPopoverHTML = document.querySelector('.agpr-chipContainer-popover') as HTMLElement;
            if (!chipPopoverHTML) {
                chipPopoverHTML = document.querySelector('.agpr-chipContainer-popover--social') as HTMLElement;
            }
            if (!chipPopoverHTML) {
                return;
            }
            chipPopoverHTML.setAttribute('tabindex', '0');
            chipPopoverHTML.addEventListener('keydown', this.panelKeydownListener);
            chipPopoverHTML.focus();
        });
    }

    /**
     * Handles closing the popover on escape or when the user attempts to tab out of the panel.
     * In this case focusout wont work because we have to catch the keystroke and prevent
     * it from moving the focus out of the DOM and into the browser controls.
     * @param event
     */
    onPanelKeydown(event: KeyboardEvent): void {
        switch(event.key) {
            case ActionKeys.ESCAPE:
                this.chipPopover.hide();
                this.onHide();
                break;
            case ActionKeys.TAB:
                if (((event.target as HTMLElement).parentElement.classList.contains('agpr-chipContainer-close') && event.shiftKey)
                    || ((event.target as HTMLElement).parentElement.classList.contains('agpr-chipContainer-chipsToken-icon') && !event.shiftKey)
                    || ((event.target as HTMLElement).classList.contains('p-overlaypanel') && !event.shiftKey)) {
                    // We only want to catch tab on the close icon OR shift + tab on the container or any chip button
                    return;
                }
                event.preventDefault();
                this.chipPopover.hide();
                this.onHide();
                break;
        }
    }

    /**
     * Handles resetting the focus when the popover closes.
     * Focuses the toggle button if present, otherwise the first chip
     */
    onHide(): void {
        requestAnimationFrame(() => {
            this.chipsContainer.nativeElement.querySelector('.agpr-button')?.focus();
        });
    }
}
